import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  IconSwimming, IconDroplet, IconTemperature, IconShieldCheck,
  IconAlertTriangle, IconAlertCircle, IconTools, IconClock,
  IconUsers, IconCheck, IconX, IconEdit, IconTrash, IconSearch,
  IconPrinter, IconPlus, IconRefresh, IconInfoCircle, IconListCheck,
  IconEye, IconTrendingUp, IconCash, IconQrcode, IconCalendar,
  IconHeartHandshake, IconFileText, IconId, IconDoorExit, IconDoorEnter
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";
import * as poolApi from "../controllers/pool.controller";
import { useStoreSettings } from "../controllers/settings.controller";

export default function PoolPage() {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs";

  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(urlTab || "dashboard");
  // tabs: dashboard, admissions, pools, memberships, water_quality, cleaning, maintenance, safety, chemicals, lessons, shifts, reports
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlTab) setTab(urlTab);
  }, [urlTab]);

  // Master Data Lists
  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolId] = useState("");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [waterQualityLogs, setWaterQualityLogs] = useState([]);
  const [cleaningSchedules, setCleaningSchedules] = useState([]);
  const [cleaningLogs, setCleaningLogs] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [safetyInspections, setSafetyInspections] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [shiftHandovers, setShiftHandovers] = useState([]);
  const [reportsData, setReportsData] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [hotelGuests, setHotelGuests] = useState([]);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [modalData, setModalData] = useState(null);
  const [activeTicketForPrint, setActiveTicketForPrint] = useState(null);

  // Load Initial Data
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [poolsRes, statsRes] = await Promise.all([
        poolApi.getPools(),
        poolApi.getPoolDashboardStats()
      ]);
      const poolsList = Array.isArray(poolsRes) ? poolsRes : [];
      setPools(poolsList);
      if (poolsList?.length && !selectedPoolId) {
        setSelectedPoolId(poolsList[0].id);
      }
      setDashboardStats(statsRes || null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load swimming pool data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // Fetch Tab Specific Data
  const fetchTabData = useCallback(async () => {
    try {
      if (tab === "dashboard") {
        const stats = await poolApi.getPoolDashboardStats(selectedPoolId || null);
        setDashboardStats(stats);
        const poolsRes = await poolApi.getPools();
        setPools(Array.isArray(poolsRes) ? poolsRes : []);
      } else if (tab === "admissions") {
        const res = await poolApi.getAdmissions({ pool_id: selectedPoolId || undefined });
        setAdmissions(Array.isArray(res) ? res : []);
        // fetch hotel checked-in guests for room charge
        try {
          const hRes = await apiClient.get("/rooms/bookings/list?status=checked_in");
          const list = Array.isArray(hRes.data) ? hRes.data : (Array.isArray(hRes.data?.data) ? hRes.data.data : []);
          setHotelGuests(list);
        } catch (e) {
          try {
            const bRes = await apiClient.get("/rooms/bookings?status=checked_in");
            const list = Array.isArray(bRes.data) ? bRes.data : (Array.isArray(bRes.data?.data) ? bRes.data.data : []);
            setHotelGuests(list);
          } catch (ignore) {
            setHotelGuests([]);
          }
        }
      } else if (tab === "pools") {
        const res = await poolApi.getPools();
        setPools(Array.isArray(res) ? res : []);
      } else if (tab === "memberships") {
        const [plans, mems] = await Promise.all([
          poolApi.getMembershipPlans(),
          poolApi.getMemberships()
        ]);
        setMembershipPlans(Array.isArray(plans) ? plans : []);
        setMemberships(Array.isArray(mems) ? mems : []);
      } else if (tab === "water_quality") {
        const res = await poolApi.getWaterQualityLogs({ pool_id: selectedPoolId || undefined });
        setWaterQualityLogs(Array.isArray(res) ? res : []);
      } else if (tab === "cleaning") {
        const [scheds, logs] = await Promise.all([
          poolApi.getCleaningSchedules(selectedPoolId || undefined),
          poolApi.getCleaningLogs({ pool_id: selectedPoolId || undefined })
        ]);
        setCleaningSchedules(Array.isArray(scheds) ? scheds : []);
        setCleaningLogs(Array.isArray(logs) ? logs : []);
      } else if (tab === "maintenance") {
        const res = await poolApi.getMaintenanceRecords({ pool_id: selectedPoolId || undefined });
        setMaintenanceRecords(Array.isArray(res) ? res : []);
      } else if (tab === "safety") {
        const [insps, incs] = await Promise.all([
          poolApi.getSafetyInspections({ pool_id: selectedPoolId || undefined }),
          poolApi.getIncidents({ pool_id: selectedPoolId || undefined })
        ]);
        setSafetyInspections(Array.isArray(insps) ? insps : []);
        setIncidents(Array.isArray(incs) ? incs : []);
      } else if (tab === "chemicals") {
        const res = await poolApi.getChemicalConsumptions({ pool_id: selectedPoolId || undefined });
        setChemicals(Array.isArray(res) ? res : []);
        // fetch inventory items for chemical dosing link
        try {
          const invRes = await apiClient.get("/inventory/items");
          const items = Array.isArray(invRes.data) ? invRes.data : (Array.isArray(invRes.data?.data) ? invRes.data.data : []);
          setInventoryItems(items);
        } catch (e) {
          setInventoryItems([]);
        }
      } else if (tab === "lessons") {
        const res = await poolApi.getLessons({ pool_id: selectedPoolId || undefined });
        setLessons(Array.isArray(res) ? res : []);
      } else if (tab === "shifts") {
        const res = await poolApi.getShiftHandovers({ pool_id: selectedPoolId || undefined });
        setShiftHandovers(Array.isArray(res) ? res : []);
      } else if (tab === "reports") {
        const res = await poolApi.getPoolReports();
        setReportsData(res || null);
      }
    } catch (err) {
      console.error("Tab data fetch error:", err);
    }
  }, [tab, selectedPoolId]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  // Selected Pool Info
  const selectedPool = useMemo(() => {
    if (!selectedPoolId) return pools[0] || null;
    return pools.find((p) => String(p.id) === String(selectedPoolId)) || pools[0] || null;
  }, [pools, selectedPoolId]);

  // Handle Modal Opens
  const openModal = (type, data = null) => {
    setModalType(type);
    setModalData(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType("");
    setModalData(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // QUICK ACTIONS HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleCheckoutAdmission = async (admission) => {
    try {
      await poolApi.checkoutAdmission(admission.ticket_number || admission.id);
      toast.success(`Guest checkout confirmed for Ticket #${admission.ticket_number}`);
      // Refresh admissions & dashboard
      const res = await poolApi.getAdmissions({ pool_id: selectedPoolId || undefined });
      setAdmissions(res || []);
      const poolsRes = await poolApi.getPools();
      setPools(poolsRes || []);
      const stats = await poolApi.getPoolDashboardStats(selectedPoolId || null);
      setDashboardStats(stats);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to checkout admission");
    }
  };

  const handlePoolStatusToggle = async (pool, newStatus) => {
    try {
      await poolApi.updatePoolStatus(pool.id, newStatus, `Manual status change to ${newStatus}`);
      toast.success(`Pool status changed to ${newStatus}`);
      const poolsRes = await poolApi.getPools();
      setPools(poolsRes || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update pool status");
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen text-slate-800">
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-slate-200 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-cyan-600 text-white rounded-xl shadow-md shadow-cyan-200">
              <IconSwimming size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Swimming Pool Management</h1>
              <p className="text-xs sm:text-sm text-slate-500">
                Occupancy tracking, admissions, water safety, maintenance & coaching operations
              </p>
            </div>
          </div>
        </div>

        {/* Global Pool Selector & Quick Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {(Array.isArray(pools) && pools.length > 0) && (
            <div className="flex items-center bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm text-sm">
              <span className="text-slate-400 mr-2 text-xs font-semibold uppercase">Pool:</span>
              <select
                value={selectedPoolId}
                onChange={(e) => setSelectedPoolId(e.target.value)}
                className="bg-transparent font-medium text-slate-700 outline-none cursor-pointer"
              >
                <option value="">All Pools</option>
                {(Array.isArray(pools) ? pools : []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.status === 'open' ? '🟢 Open' : '🔴 ' + p.status})
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={() => openModal("admit_guest")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition"
          >
            <IconDoorEnter size={17} />
            <span>Admit Guest</span>
          </button>

          <button
            onClick={() => openModal("scan_qr")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg shadow-sm transition"
          >
            <IconQrcode size={17} />
            <span>Scan / Validate</span>
          </button>

          <button
            onClick={() => openModal("occupancy_correction")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg shadow-sm transition"
          >
            <IconUsers size={17} />
            <span>Adjust Headcount</span>
          </button>

          <button
            onClick={loadInitialData}
            title="Refresh"
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition"
          >
            <IconRefresh size={18} />
          </button>
        </div>
      </div>

      {/* ── SAFETY / STATUS BANNER ── */}
      {selectedPool && selectedPool.status !== "open" && (
        <div className="mt-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3 text-red-800 shadow-sm animate-pulse">
          <div className="flex items-center gap-3">
            <IconAlertTriangle className="text-red-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-sm">
                Attention: {selectedPool.name} is currently {selectedPool.status.toUpperCase().replace('_', ' ')}
              </p>
              <p className="text-xs text-red-700">{selectedPool.status_reason || "Access restricted for guest safety"}</p>
            </div>
          </div>
          <button
            onClick={() => handlePoolStatusToggle(selectedPool, "open")}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg transition"
          >
            Reopen Pool
          </button>
        </div>
      )}

      {/* ── NAVIGATION TABS ── */}
      <div className="mt-4 flex items-center gap-1 overflow-x-auto border-b border-slate-200 pb-px scrollbar-none text-sm font-medium">
        {[
          { id: "dashboard", label: "Live Dashboard", icon: IconTrendingUp },
          { id: "admissions", label: "Admissions & Gate", icon: IconDoorEnter },
          { id: "pools", label: "Pools & Schedule", icon: IconSwimming },
          { id: "memberships", label: "Memberships", icon: IconId },
          { id: "water_quality", label: "Water Quality & Chemicals", icon: IconDroplet },
          { id: "cleaning", label: "Cleaning Schedules", icon: IconListCheck },
          { id: "maintenance", label: "Equipment Maintenance", icon: IconTools },
          { id: "safety", label: "Safety & Incidents", icon: IconShieldCheck },
          { id: "chemicals", label: "Chemical Consumables", icon: IconDroplet },
          { id: "lessons", label: "Swimming Lessons", icon: IconUsers },
          { id: "shifts", label: "Shift Handovers", icon: IconClock },
          { id: "reports", label: "Analytics & Reports", icon: IconFileText }
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-lg transition whitespace-nowrap border-b-2 font-medium ${
                active
                  ? "border-cyan-600 text-cyan-700 bg-white shadow-xs font-semibold"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              }`}
            >
              <Icon size={17} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── TAB CONTENT ── */}
      <div className="mt-4">
        {/* 1. LIVE DASHBOARD TAB */}
        {tab === "dashboard" && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Swimmers Now</p>
                  <p className="text-3xl font-extrabold text-cyan-600 mt-1">
                    {dashboardStats?.active_swimmers_now || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Total pool capacity: {dashboardStats?.total_capacity || 0}
                  </p>
                </div>
                <div className="p-3 bg-cyan-50 text-cyan-600 rounded-xl">
                  <IconSwimming size={26} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Today's Swimmers</p>
                  <p className="text-3xl font-extrabold text-slate-800 mt-1">
                    {dashboardStats?.total_swimmers_today || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {dashboardStats?.total_tickets_today || 0} tickets processed
                  </p>
                </div>
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <IconDoorEnter size={26} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Today's Revenue</p>
                  <p className="text-3xl font-extrabold text-emerald-600 mt-1">
                    {currency} {Number(dashboardStats?.today_admission_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Admissions & rentals</p>
                </div>
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                  <IconCash size={26} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Members</p>
                  <p className="text-3xl font-extrabold text-indigo-600 mt-1">
                    {dashboardStats?.active_members || 0}
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5 font-medium">
                    {dashboardStats?.members_expiring_soon || 0} expiring this week
                  </p>
                </div>
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                  <IconId size={26} />
                </div>
              </div>
            </div>

            {/* Live Pool Capacity Gauges Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {(Array.isArray(pools) ? pools : []).map((p) => {
                const maxCap = p.max_capacity || 50;
                const occ = p.current_occupancy || 0;
                const pct = Math.min(100, Math.round((occ / maxCap) * 100));
                const isWarning = pct >= 70 && pct < 90;
                const isFull = pct >= 90;
                const colorClass = isFull ? "text-red-600" : isWarning ? "text-amber-500" : "text-emerald-500";
                const bgBarClass = isFull ? "bg-red-500" : isWarning ? "bg-amber-400" : "bg-emerald-500";

                return (
                  <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-slate-900 text-base">{p.name}</h3>
                          <p className="text-xs text-slate-400 capitalize">{p.pool_type?.replace('_', ' ')} • {p.location || 'Main Deck'}</p>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                          p.status === 'open' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {p.status}
                        </span>
                      </div>

                      {/* Circular Gauge / Large Occupancy Counter */}
                      <div className="my-4 flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                        <div>
                          <p className="text-xs font-semibold text-slate-400 uppercase">Live Swimmers</p>
                          <div className="flex items-baseline gap-1 mt-1">
                            <span className={`text-4xl font-extrabold ${colorClass}`}>{occ}</span>
                            <span className="text-lg font-bold text-slate-400">/ {maxCap}</span>
                          </div>
                          <p className="text-xs font-medium text-slate-500 mt-1">
                            {p.slots_remaining} slots remaining
                          </p>
                        </div>

                        {/* Progress ring percentage badge */}
                        <div className="text-center">
                          <div className="relative w-16 h-16 flex items-center justify-center rounded-full bg-white border-4 border-slate-100 shadow-inner">
                            <span className={`text-base font-extrabold ${colorClass}`}>{pct}%</span>
                          </div>
                          <span className="text-[10px] font-semibold text-slate-400 uppercase mt-1 block">Capacity</span>
                        </div>
                      </div>

                      {/* Capacity progress bar */}
                      <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden mb-3">
                        <div className={`h-full ${bgBarClass} transition-all duration-500`} style={{ width: `${pct}%` }} />
                      </div>

                      <div className="text-xs text-slate-500 flex justify-between pt-2 border-t border-slate-100">
                        <span>Hours: {p.opening_time} - {p.closing_time}</span>
                        <span>Adult: {currency} {p.adult_ticket_price}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <button
                        onClick={() => {
                          setSelectedPoolId(p.id);
                          openModal("admit_guest", { pool_id: p.id });
                        }}
                        disabled={p.status !== 'open'}
                        className="flex-1 py-1.5 px-3 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition text-center"
                      >
                        + Admit Swimmers
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPoolId(p.id);
                          setTab("admissions");
                        }}
                        className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition"
                      >
                        View Gate
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 2. ADMISSIONS & QR GATE TAB */}
        {tab === "admissions" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <IconSearch className="absolute left-3 top-2.5 text-slate-400" size={17} />
                  <input
                    type="text"
                    placeholder="Search by ticket #, guest name, phone, room..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-cyan-600"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Inside Pool</option>
                  <option value="completed">Completed / Exited</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openModal("admit_guest")}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition"
                >
                  <IconPlus size={16} />
                  <span>Issue New Ticket</span>
                </button>
              </div>
            </div>

            {/* Admissions Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Ticket / Pass</th>
                      <th className="py-3 px-4">Guest / Swimmer</th>
                      <th className="py-3 px-4">Pool & Type</th>
                      <th className="py-3 px-4 text-center">Swimmers</th>
                      <th className="py-3 px-4">Entry / Exit</th>
                      <th className="py-3 px-4">Billing & Fee</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(Array.isArray(admissions) ? admissions : [])
                      .filter((a) => {
                        if (statusFilter !== "all" && a.admission_status !== statusFilter) return false;
                        if (searchQuery) {
                          const q = searchQuery.toLowerCase();
                          return (
                            a.ticket_number?.toLowerCase().includes(q) ||
                            a.guest_name?.toLowerCase().includes(q) ||
                            a.guest_phone?.toLowerCase().includes(q) ||
                            a.room_number?.toLowerCase().includes(q)
                          );
                        }
                        return true;
                      })
                      .map((a) => {
                        const isActive = a.admission_status === "active";
                        return (
                          <tr key={a.id} className="hover:bg-slate-50/80 transition">
                            <td className="py-3 px-4">
                              <span className="font-bold text-slate-800">{a.ticket_number}</span>
                              <div className="text-[11px] text-slate-400 font-mono mt-0.5 truncate max-w-[120px]">
                                {a.qr_code_token}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-semibold text-slate-800">{a.guest_name}</p>
                              <p className="text-xs text-slate-500">
                                {a.guest_phone || (a.room_number ? `Room ${a.room_number}` : 'Walk-in')}
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-800">{a.pool_name}</p>
                              <span className="text-[11px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded uppercase font-semibold">
                                {a.admission_type?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full font-bold bg-cyan-50 text-cyan-700 text-xs">
                                {a.total_swimmers} ({a.adults_count}A + {a.children_count}C)
                              </span>
                              {(a.towel_rental_count > 0 || a.locker_rental_count > 0) && (
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  {a.towel_rental_count > 0 ? `${a.towel_rental_count} towels ` : ''}
                                  {a.locker_rental_count > 0 ? `${a.locker_rental_count} lockers` : ''}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-xs text-slate-600">
                              <div>In: {a.entry_time ? new Date(a.entry_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                              {a.exit_time && (
                                <div className="text-slate-400">Out: {new Date(a.exit_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-bold text-slate-800">{currency} {Number(a.total_amount).toLocaleString()}</p>
                              <span className="text-[11px] text-slate-400 capitalize">
                                {a.payment_method?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                                isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                a.admission_status === 'completed' ? 'bg-slate-100 text-slate-600' : 'bg-red-50 text-red-700'
                              }`}>
                                {a.admission_status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {isActive && (
                                  <button
                                    onClick={() => handleCheckoutAdmission(a)}
                                    title="Check-out & Free Slot"
                                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold text-xs rounded-lg transition border border-amber-200"
                                  >
                                    Exit
                                  </button>
                                )}
                                <button
                                  onClick={() => setActiveTicketForPrint(a)}
                                  title="Print Ticket"
                                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition"
                                >
                                  <IconPrinter size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 3. POOLS MASTER & OPERATING HOURS TAB */}
        {tab === "pools" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Swimming Pools Configuration</h2>
              <button
                onClick={() => openModal("add_pool")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={16} />
                <span>Add New Pool</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {(Array.isArray(pools) ? pools : []).map((p) => (
                <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-slate-900 text-base">{p.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                        p.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">{p.description || "Main hotel facility pool"}</p>

                    <div className="space-y-2 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Type / Location:</span>
                        <span className="font-semibold">{p.pool_type} • {p.location || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Dimensions:</span>
                        <span className="font-semibold">{p.length_meters || 25}m × {p.width_meters || 12}m (Depth: {p.depth_min_meters || 1.2}m - {p.depth_max_meters || 2.4}m)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Volume:</span>
                        <span className="font-semibold">{Number(p.volume_liters || 100000).toLocaleString()} Liters</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Max Permitted Capacity:</span>
                        <span className="font-semibold text-cyan-700">{p.max_capacity} Swimmers</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Operating Hours:</span>
                        <span className="font-semibold">{p.opening_time} - {p.closing_time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Ticket Rates:</span>
                        <span className="font-semibold">Adult: {currency} {p.adult_ticket_price} | Child: {currency} {p.child_ticket_price}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => openModal("edit_pool", p)}
                      className="flex-1 py-1 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition text-center"
                    >
                      Edit Pool
                    </button>
                    <select
                      value={p.status}
                      onChange={(e) => handlePoolStatusToggle(p, e.target.value)}
                      className="py-1 px-2 text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg outline-none cursor-pointer"
                    >
                      <option value="open">🟢 Open</option>
                      <option value="temporarily_closed">🟡 Temp Closed</option>
                      <option value="maintenance">🔧 Maintenance</option>
                      <option value="emergency_closure">🔴 Emergency</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. MEMBERSHIPS & PLANS TAB */}
        {tab === "memberships" && (
          <div className="space-y-6">
            {/* Membership Plans List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-slate-800">Configured Membership Plans</h2>
                <button
                  onClick={() => openModal("add_plan")}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
                >
                  <IconPlus size={15} />
                  <span>New Plan</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(Array.isArray(membershipPlans) ? membershipPlans : []).map((plan) => (
                  <div key={plan.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-bold text-slate-900 text-sm">{plan.name}</h3>
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded">
                          {plan.duration_type}
                        </span>
                      </div>
                      <p className="text-2xl font-extrabold text-cyan-600 mt-2">
                        {currency} {Number(plan.price).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{plan.duration_days} days validity</p>

                      <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                        <li className="flex items-center gap-1.5">
                          <IconCheck size={14} className="text-emerald-500" />
                          <span>{plan.max_daily_admissions} daily admission(s)</span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <IconCheck size={14} className="text-emerald-500" />
                          <span>{plan.guest_privileges_count} guest privilege(s)</span>
                        </li>
                        {plan.free_towel_rental ? (
                          <li className="flex items-center gap-1.5 text-emerald-700 font-medium">
                            <IconCheck size={14} className="text-emerald-500" />
                            <span>Free towel service</span>
                          </li>
                        ) : null}
                      </ul>
                    </div>

                    <button
                      onClick={() => openModal("new_member", { plan_id: plan.id, price: plan.price, duration_days: plan.duration_days })}
                      className="mt-4 w-full py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 font-semibold text-xs rounded-lg transition"
                    >
                      Enroll Member
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Members Directory */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-slate-800">Members Directory</h2>
                <button
                  onClick={() => openModal("new_member")}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
                >
                  <IconPlus size={15} />
                  <span>Register Member</span>
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="py-3 px-4">Member Card #</th>
                        <th className="py-3 px-4">Member Details</th>
                        <th className="py-3 px-4">Plan</th>
                        <th className="py-3 px-4">Validity</th>
                        <th className="py-3 px-4">Fee / Balance</th>
                        <th className="py-3 px-4 text-center">Status</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(Array.isArray(memberships) ? memberships : []).map((m) => (
                        <tr key={m.id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3 px-4">
                            <span className="font-bold text-slate-800">{m.membership_number}</span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-slate-800">{m.member_name}</p>
                            <p className="text-xs text-slate-500">{m.member_phone} {m.member_email ? `• ${m.member_email}` : ''}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium text-slate-800">{m.plan_name}</span>
                            <div className="text-xs text-slate-400 capitalize">{m.plan_type}</div>
                          </td>
                          <td className="py-3 px-4 text-xs">
                            <div>From: {m.start_date}</div>
                            <div className="font-semibold text-slate-700">Expires: {m.expiry_date}</div>
                          </td>
                          <td className="py-3 px-4 text-xs">
                            <div className="font-bold text-slate-800">{currency} {Number(m.total_amount).toLocaleString()}</div>
                            {Number(m.balance_due) > 0 && (
                              <div className="text-red-600 font-semibold">Due: {currency} {Number(m.balance_due).toLocaleString()}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              m.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                              m.status === 'frozen' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700'
                            }`}>
                              {m.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {m.status === 'active' ? (
                                <button
                                  onClick={() => openModal("freeze_member", m)}
                                  className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded transition"
                                >
                                  Freeze
                                </button>
                              ) : null}
                              <button
                                onClick={() => openModal("renew_member", m)}
                                className="px-2.5 py-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 font-semibold text-xs rounded-lg transition"
                              >
                                Renew
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 5. WATER QUALITY & CHEMICAL TESTS TAB */}
        {tab === "water_quality" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Water Quality & Chemical Monitoring</h2>
                <p className="text-xs text-slate-500">
                  Target standards: pH 7.2 - 7.8 | Free Chlorine 1.0 - 3.0 ppm | Temp 26 - 30°C
                </p>
              </div>
              <button
                onClick={() => openModal("log_water_test")}
                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={16} />
                <span>Log Water Quality Test</span>
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Test Timestamp</th>
                      <th className="py-3 px-4">Pool & Tester</th>
                      <th className="py-3 px-4 text-center">pH Level</th>
                      <th className="py-3 px-4 text-center">Free Chlorine (ppm)</th>
                      <th className="py-3 px-4 text-center">Total Chlorine</th>
                      <th className="py-3 px-4 text-center">Water Temp</th>
                      <th className="py-3 px-4 text-center">Clarity</th>
                      <th className="py-3 px-4 text-center">Safety Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(Array.isArray(waterQualityLogs) ? waterQualityLogs : []).map((log) => {
                      const isNormal = log.status === 'normal';
                      const isHazard = log.status === 'critical_hazard';
                      return (
                        <tr key={log.id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3 px-4 text-xs text-slate-600">
                            {new Date(log.test_timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-slate-800">{log.pool_name}</p>
                            <p className="text-xs text-slate-400">By {log.tested_by}</p>
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-slate-800">
                            <span className={log.ph_level < 7.2 || log.ph_level > 7.8 ? 'text-red-600 font-extrabold' : 'text-slate-800'}>
                              {log.ph_level}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-slate-800">
                            <span className={log.free_chlorine_ppm < 1.0 || log.free_chlorine_ppm > 3.0 ? 'text-red-600 font-extrabold' : 'text-slate-800'}>
                              {log.free_chlorine_ppm} ppm
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-xs text-slate-600">
                            {log.total_chlorine_ppm || '-'} ppm
                          </td>
                          <td className="py-3 px-4 text-center text-xs text-slate-600">
                            {log.water_temperature_c ? `${log.water_temperature_c}°C` : '-'}
                          </td>
                          <td className="py-3 px-4 text-center text-xs capitalize">
                            <span className={`px-2 py-0.5 rounded font-medium ${
                              log.water_clarity === 'clear' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {log.water_clarity}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              isNormal ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                              isHazard ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {log.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {!isNormal && !log.retest_status && (
                              <button
                                onClick={() => openModal("water_retest", log)}
                                className="px-2.5 py-1 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs rounded-lg transition"
                              >
                                Log Retest
                              </button>
                            )}
                            {log.retest_status && (
                              <span className="text-xs text-emerald-600 font-semibold">
                                Retested: {log.retest_status}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 6. CLEANING SCHEDULES TAB */}
        {tab === "cleaning" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Recurring Cleaning Tasks</h2>
              <button
                onClick={() => openModal("add_cleaning_schedule")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={15} />
                <span>Add Cleaning Task</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Array.isArray(cleaningSchedules) ? cleaningSchedules : []).map((task) => (
                <div key={task.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-slate-900 text-sm">{task.task_name}</h3>
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {task.frequency}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Pool: {task.pool_name} • Scheduled: {task.scheduled_time || '07:00'}</p>
                    <p className="text-xs text-slate-400 mt-1">Assigned: {task.assigned_staff_name || 'Attendant'}</p>

                    <div className="mt-3 p-2 bg-slate-50 rounded text-xs text-slate-600">
                      Last completed: {task.last_completed_at ? new Date(task.last_completed_at).toLocaleString() : 'Pending completion'}
                    </div>
                  </div>

                  <button
                    onClick={() => openModal("complete_cleaning", task)}
                    className="mt-4 w-full py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold text-xs rounded-lg transition"
                  >
                    Mark Task Completed
                  </button>
                </div>
              ))}
            </div>

            {/* Cleaning Audit Log */}
            <div>
              <h3 className="text-base font-bold text-slate-800 mb-2">Recent Cleaning Logs</h3>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
                      <th className="py-2.5 px-4">Task Name</th>
                      <th className="py-2.5 px-4">Pool</th>
                      <th className="py-2.5 px-4">Completed At</th>
                      <th className="py-2.5 px-4">Completed By</th>
                      <th className="py-2.5 px-4">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {(Array.isArray(cleaningLogs) ? cleaningLogs : []).slice(0, 10).map((l) => (
                      <tr key={l.id}>
                        <td className="py-2.5 px-4 font-semibold text-slate-800">{l.task_name}</td>
                        <td className="py-2.5 px-4 text-slate-600">{l.pool_name}</td>
                        <td className="py-2.5 px-4 text-slate-500">{new Date(l.completed_at).toLocaleString()}</td>
                        <td className="py-2.5 px-4 text-slate-700">{l.completed_by}</td>
                        <td className="py-2.5 px-4 text-slate-500">{l.remarks || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 7. EQUIPMENT MAINTENANCE TAB */}
        {tab === "maintenance" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Equipment Maintenance & Downtime Tracking</h2>
              <button
                onClick={() => openModal("add_maintenance")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={15} />
                <span>Report Issue / Service</span>
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Equipment & Pool</th>
                      <th className="py-3 px-4">Issue Description</th>
                      <th className="py-3 px-4 text-center">Priority</th>
                      <th className="py-3 px-4">Assigned / Vendor</th>
                      <th className="py-3 px-4 text-center">Downtime</th>
                      <th className="py-3 px-4">Cost</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(Array.isArray(maintenanceRecords) ? maintenanceRecords : []).map((m) => (
                      <tr key={m.id} className="hover:bg-slate-50/80 transition">
                        <td className="py-3 px-4">
                          <p className="font-semibold text-slate-800">{m.equipment_name}</p>
                          <p className="text-xs text-slate-400">{m.pool_name} • {m.maintenance_type}</p>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-700 max-w-xs">
                          {m.issue_description}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                            m.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            m.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {m.priority}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs">
                          <div>{m.assigned_to || m.vendor_name || 'In-house'}</div>
                          {m.vendor_contact && <div className="text-slate-400">{m.vendor_contact}</div>}
                        </td>
                        <td className="py-3 px-4 text-center text-xs font-bold text-slate-700">
                          {m.downtime_hours > 0 ? `${m.downtime_hours} hrs` : '-'}
                        </td>
                        <td className="py-3 px-4 text-xs font-bold text-slate-800">
                          {m.cost > 0 ? `${currency} ${Number(m.cost).toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            m.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {m.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {m.status !== 'completed' && (
                            <button
                              onClick={() => openModal("update_maintenance", m)}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded transition"
                            >
                              Update / Resolve
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 8. SAFETY & INCIDENTS TAB */}
        {tab === "safety" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Safety Inspections & Incident Management</h2>
                <p className="text-xs text-slate-500">Lifeguard daily opening checklists and incident register</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openModal("new_safety_inspection")}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
                >
                  <IconShieldCheck size={15} />
                  <span>Log Safety Inspection</span>
                </button>
                <button
                  onClick={() => openModal("new_incident")}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
                >
                  <IconAlertTriangle size={15} />
                  <span>Report Incident</span>
                </button>
              </div>
            </div>

            {/* Incidents Table */}
            <div>
              <h3 className="text-base font-bold text-slate-800 mb-2">Incidents & Near-Miss Register</h3>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Incident #</th>
                      <th className="py-3 px-4">Pool & Time</th>
                      <th className="py-3 px-4">Person Involved</th>
                      <th className="py-3 px-4 text-center">Severity</th>
                      <th className="py-3 px-4">Action Taken</th>
                      <th className="py-3 px-4 text-center">Closure</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(Array.isArray(incidents) ? incidents : []).map((inc) => (
                      <tr key={inc.id} className="hover:bg-slate-50/80 transition">
                        <td className="py-3 px-4 font-bold text-slate-800">{inc.incident_number}</td>
                        <td className="py-3 px-4 text-xs">
                          <div className="font-semibold text-slate-800">{inc.pool_name}</div>
                          <div className="text-slate-400">{new Date(inc.incident_time).toLocaleString()}</div>
                        </td>
                        <td className="py-3 px-4 text-xs">
                          <p className="font-semibold text-slate-800">{inc.person_name || 'Guest'}</p>
                          <p className="text-slate-400">{inc.person_involved_type} {inc.room_number ? `(Room ${inc.room_number})` : ''}</p>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                            inc.severity === 'critical' || inc.severity === 'severe' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {inc.severity}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-700 max-w-xs">{inc.immediate_action_taken}</td>
                        <td className="py-3 px-4 text-center text-xs">
                          {inc.pool_temporarily_closed ? (
                            <span className="text-red-600 font-bold">Closed</span>
                          ) : (
                            <span className="text-slate-400">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            inc.investigation_status === 'resolved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {inc.investigation_status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {inc.investigation_status === 'open' && (
                            <button
                              onClick={() => openModal("reopen_incident", inc)}
                              className="px-2.5 py-1 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs rounded-lg transition"
                            >
                              Authorize Resolution
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 9. CHEMICALS & INVENTORY TAB */}
        {tab === "chemicals" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Chemical Consumables & Dosing Logs</h2>
                <p className="text-xs text-slate-500">Linked to hotel central inventory for stock deduction</p>
              </div>
              <button
                onClick={() => openModal("log_chemical_dosing")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={15} />
                <span>Log Chemical Dosing</span>
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
                    <th className="py-3 px-4">Chemical Name</th>
                    <th className="py-3 px-4">Pool</th>
                    <th className="py-3 px-4 text-center">Quantity Consumed</th>
                    <th className="py-3 px-4">Purpose</th>
                    <th className="py-3 px-4">Administered By</th>
                    <th className="py-3 px-4">Inventory Link</th>
                    <th className="py-3 px-4">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {(Array.isArray(chemicals) ? chemicals : []).map((c) => (
                    <tr key={c.id}>
                      <td className="py-3 px-4 font-bold text-slate-800">{c.chemical_name}</td>
                      <td className="py-3 px-4 text-slate-700">{c.pool_name}</td>
                      <td className="py-3 px-4 text-center font-bold text-cyan-700">
                        {c.quantity_consumed} {c.unit}
                      </td>
                      <td className="py-3 px-4 capitalize text-slate-600">{c.purpose?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-slate-700">{c.administered_by}</td>
                      <td className="py-3 px-4 text-slate-500">
                        {c.inventory_title ? `${c.inventory_title} (rem: ${c.remaining_stock})` : 'Manual Log'}
                      </td>
                      <td className="py-3 px-4 text-slate-400">{new Date(c.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 10. SWIMMING LESSONS TAB */}
        {tab === "lessons" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Swimming Coaching & Lessons</h2>
              <button
                onClick={() => openModal("add_lesson")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={15} />
                <span>Create Lesson Batch</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {(Array.isArray(lessons) ? lessons : []).map((les) => (
                <div key={les.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-slate-900 text-base">{les.title}</h3>
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded">
                        {les.skill_level}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Instructor: {les.instructor_name} {les.instructor_phone ? `(${les.instructor_phone})` : ''}</p>

                    <div className="mt-3 space-y-1 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <div>Schedule: {les.schedule_days} ({les.start_time} - {les.end_time})</div>
                      <div>Dates: {les.start_date} to {les.end_date}</div>
                      <div>Fee: <span className="font-bold text-slate-800">{currency} {Number(les.price_per_student).toLocaleString()}</span> per student</div>
                      <div className="font-semibold text-cyan-700">Enrolled: {les.enrolled_students || 0} / {les.max_students} students</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <button
                      onClick={() => openModal("enroll_student", les)}
                      disabled={Number(les.enrolled_students || 0) >= Number(les.max_students || 10)}
                      className="flex-1 py-1.5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-semibold text-xs rounded-lg transition"
                    >
                      + Enroll Student
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 11. SHIFT HANDOVERS TAB */}
        {tab === "shifts" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Lifeguard Shift Handovers</h2>
                <p className="text-xs text-slate-500">Opening/closing turnover logs and operational status transfer</p>
              </div>
              <button
                onClick={() => openModal("new_shift_handover")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPlus size={15} />
                <span>Log Shift Handover</span>
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
                    <th className="py-3 px-4">Shift Name</th>
                    <th className="py-3 px-4">Pool</th>
                    <th className="py-3 px-4">Outgoing & Incoming Staff</th>
                    <th className="py-3 px-4 text-center">Occupancy at Handover</th>
                    <th className="py-3 px-4">Water / Equipment Status</th>
                    <th className="py-3 px-4">Pending Tasks / Notes</th>
                    <th className="py-3 px-4">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {(Array.isArray(shiftHandovers) ? shiftHandovers : []).map((s) => (
                    <tr key={s.id}>
                      <td className="py-3 px-4 font-bold text-slate-800">{s.shift_name}</td>
                      <td className="py-3 px-4 text-slate-700">{s.pool_name}</td>
                      <td className="py-3 px-4 text-slate-700">
                        <div>From: <span className="font-semibold">{s.handing_over_staff}</span></div>
                        <div>To: <span className="font-semibold">{s.receiving_staff}</span></div>
                      </td>
                      <td className="py-3 px-4 text-center font-bold text-cyan-700">{s.current_occupancy} Swimmers</td>
                      <td className="py-3 px-4 text-slate-600">
                        <div>Water: {s.water_quality_status}</div>
                        <div>Equip: {s.equipment_status}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-500 max-w-xs">{s.pending_tasks_for_next_shift || '-'}</td>
                      <td className="py-3 px-4 text-slate-400">{new Date(s.handover_timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 12. ANALYTICS & REPORTS TAB */}
        {tab === "reports" && reportsData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Swimming Pool Operational Analytics</h2>
                <p className="text-xs text-slate-500">Period: {reportsData.from_date} to {reportsData.to_date}</p>
              </div>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm transition"
              >
                <IconPrinter size={15} />
                <span>Print Report</span>
              </button>
            </div>

            {/* Revenue by Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                <h3 className="font-bold text-slate-800 text-sm mb-3">Revenue by Admission Type</h3>
                <div className="space-y-3">
                  {(Array.isArray(reportsData?.revenue_by_type) ? reportsData.revenue_by_type : []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-slate-100 pb-2">
                      <span className="font-semibold text-slate-700 capitalize">{r.admission_type?.replace('_', ' ')}</span>
                      <div className="text-right">
                        <span className="font-bold text-slate-900">{currency} {Number(r.total_revenue).toLocaleString()}</span>
                        <span className="text-slate-400 ml-2">({r.admissions_count} tickets)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                <h3 className="font-bold text-slate-800 text-sm mb-3">Chemical Dosing Totals</h3>
                <div className="space-y-3">
                  {(Array.isArray(reportsData?.chemical_usage) ? reportsData.chemical_usage : []).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-slate-100 pb-2">
                      <span className="font-semibold text-slate-700">{c.chemical_name}</span>
                      <span className="font-bold text-cyan-700">{c.total_quantity} {c.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODALS */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl md:max-w-2xl border border-slate-100 overflow-hidden my-8 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
              <h3 className="font-bold text-slate-800 text-base">
                {modalType === "add_pool" ? "Add New Swimming Pool" :
                 modalType === "edit_pool" ? "Edit Swimming Pool Details" :
                 modalType === "add_plan" ? "Create Membership Plan" :
                 modalType === "new_member" ? "Register Pool Member" :
                 modalType === "renew_member" ? "Renew Membership" :
                 modalType === "freeze_member" ? "Freeze Membership" :
                 modalType === "add_cleaning_schedule" ? "Schedule Cleaning Task" :
                 modalType === "complete_cleaning" ? "Complete Cleaning Task" :
                 modalType === "add_maintenance" ? "Log Equipment Issue / Service" :
                 modalType === "update_maintenance" ? "Update Equipment Maintenance" :
                 modalType === "new_safety_inspection" ? "Log Safety Inspection Checklist" :
                 modalType === "new_incident" ? "Report Pool Incident / Near-Miss" :
                 modalType === "reopen_incident" ? "Authorize Incident Resolution" :
                 modalType === "log_chemical_dosing" ? "Log Chemical Consumption & Dosing" :
                 modalType === "add_lesson" ? "Create Swimming Lesson Batch" :
                 modalType === "enroll_student" ? "Enroll Student in Lesson" :
                 modalType === "new_shift_handover" ? "Lifeguard Shift Handover" :
                 modalType === "admit_guest" ? "Issue Ticket & Admit Swimmer" :
                 modalType === "scan_qr" ? "Scan Ticket / Validate Gate Pass" :
                 modalType === "occupancy_correction" ? "Audit Headcount Adjustment" :
                 modalType === "log_water_test" ? "Record Pool Water Quality Test" :
                 modalType === "water_retest" ? "Log Water Retest Follow-up" :
                 modalType.replace(/_/g, ' ')}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 transition">
                <IconX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* --- ADMIT GUEST MODAL --- */}
              {modalType === "admit_guest" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      admission_type: formData.get("admission_type"),
                      guest_name: formData.get("guest_name"),
                      guest_phone: formData.get("guest_phone"),
                      adults_count: Number(formData.get("adults_count") || 1),
                      children_count: Number(formData.get("children_count") || 0),
                      hotel_booking_id: formData.get("hotel_booking_id") || null,
                      room_number: formData.get("room_number") || null,
                      towel_rental_count: Number(formData.get("towel_rental_count") || 0),
                      locker_rental_count: Number(formData.get("locker_rental_count") || 0),
                      discount: Number(formData.get("discount") || 0),
                      payment_method: formData.get("payment_method") || "cash"
                    };

                    try {
                      const res = await poolApi.createAdmissionTicket(payload);
                      toast.success(`Ticket issued: #${res.ticket_number}`);
                      closeModal();
                      // Refresh admissions & dashboard
                      const adms = await poolApi.getAdmissions({ pool_id: selectedPoolId || undefined });
                      setAdmissions(Array.isArray(adms) ? adms : []);
                      const stats = await poolApi.getPoolDashboardStats(selectedPoolId || null);
                      setDashboardStats(stats);
                      const poolsRes = await poolApi.getPools();
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                      // Show printable pass
                      setActiveTicketForPrint({ ...payload, ...res, pool_name: selectedPool?.name });
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to admit guest");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Select Pool</label>
                    <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      {(Array.isArray(pools) ? pools : []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.status === 'open' ? 'Open' : p.status}) - {p.slots_remaining} slots free
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Admission Type</label>
                    <select name="admission_type" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      <option value="walk_in">Walk-in Visitor</option>
                      <option value="hotel_guest">In-House Hotel Guest (Room Charge)</option>
                      <option value="member">Registered Member</option>
                      <option value="day_pass">Day Pass</option>
                      <option value="complimentary">Complimentary / VIP</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Guest Name *</label>
                      <input name="guest_name" required placeholder="Guest full name" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Guest Phone</label>
                      <input name="guest_phone" placeholder="Phone number" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Hotel Booking (For Room Charge)</label>
                      <select name="hotel_booking_id" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="">None / Not Hotel Guest</option>
                        {(Array.isArray(hotelGuests) ? hotelGuests : []).map((h) => (
                          <option key={h.id} value={h.id}>
                            Room {h.room_number || h.room_title || h.id} - {h.customer_name || h.guest_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Room Number</label>
                      <input name="room_number" placeholder="e.g. 204" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Adults Count</label>
                      <input type="number" name="adults_count" defaultValue={1} min={1} className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Children Count</label>
                      <input type="number" name="children_count" defaultValue={0} min={0} className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Towel Rentals (Qty)</label>
                      <input type="number" name="towel_rental_count" defaultValue={0} min={0} className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Locker Rentals (Qty)</label>
                      <input type="number" name="locker_rental_count" defaultValue={0} min={0} className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Method</label>
                      <select name="payment_method" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="cash">Cash</option>
                        <option value="card">Credit / Debit Card</option>
                        <option value="qr_pay">QR Payment / Mobile Banking</option>
                        <option value="room_charge">Room Folio Charge</option>
                        <option value="complimentary">Complimentary</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Discount Amount</label>
                      <input type="number" name="discount" defaultValue={0} min={0} className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Issue Ticket & Admit
                    </button>
                  </div>
                </form>
              )}

              {/* --- SCAN / VALIDATE MODAL --- */}
              {modalType === "scan_qr" && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">
                    Enter or scan the ticket number, QR code token, or membership number:
                  </p>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const val = e.target.search_token.value.trim();
                      if (!val) return;
                      try {
                        const res = await poolApi.checkoutAdmission(val);
                        toast.success(res.message || "Action processed successfully");
                        closeModal();
                        const adms = await poolApi.getAdmissions({ pool_id: selectedPoolId || undefined });
                        setAdmissions(Array.isArray(adms) ? adms : []);
                        const stats = await poolApi.getPoolDashboardStats(selectedPoolId || null);
                        setDashboardStats(stats);
                      } catch (err) {
                        toast.error(err.response?.data?.message || err.message || "Failed to process token");
                      }
                    }}
                    className="space-y-3"
                  >
                    <input
                      name="search_token"
                      autoFocus
                      placeholder="e.g. TKT-20261023-ABC or POOL-xxx..."
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono"
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs rounded-lg transition">
                        Validate & Free Slot (Check-Out)
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* --- OCCUPANCY CORRECTION MODAL --- */}
              {modalType === "occupancy_correction" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const poolId = formData.get("pool_id") || selectedPoolId;
                    const corrected = formData.get("corrected_occupancy");
                    const reason = formData.get("reason");

                    try {
                      const res = await poolApi.recordOccupancyCorrection(poolId, {
                        corrected_occupancy: corrected,
                        reason: reason
                      });
                      toast.success(`Headcount updated to ${res.corrected_occupancy} (Delta: ${res.delta > 0 ? '+' : ''}${res.delta})`);
                      closeModal();
                      const poolsRes = await poolApi.getPools();
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                      const stats = await poolApi.getPoolDashboardStats(poolId);
                      setDashboardStats(stats);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to adjust occupancy");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Select Pool</label>
                    <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      {(Array.isArray(pools) ? pools : []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} (Current Occupancy: {p.current_occupancy})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Actual Physical Headcount (Swimmers) *</label>
                    <input type="number" name="corrected_occupancy" required min={0} placeholder="Count of people currently in water" className="w-full p-2 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Reason for Audit Adjustment *</label>
                    <textarea name="reason" required placeholder="e.g. Swimmer exited without scanning ticket / manual head count verification" className="w-full p-2 border border-slate-200 rounded-lg" rows={3} />
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Save Correction
                    </button>
                  </div>
                </form>
              )}

              {/* --- LOG WATER TEST MODAL --- */}
              {modalType === "log_water_test" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      ph_level: formData.get("ph_level"),
                      free_chlorine_ppm: formData.get("free_chlorine_ppm"),
                      total_chlorine_ppm: formData.get("total_chlorine_ppm"),
                      water_temperature_c: formData.get("water_temperature_c"),
                      water_clarity: formData.get("water_clarity"),
                      remarks: formData.get("remarks")
                    };

                    try {
                      const res = await poolApi.createWaterQualityLog(payload);
                      if (res.pool_closed) {
                        toast.error("Safety hazard detected! Pool has been automatically closed for treatment.");
                      } else {
                        toast.success("Water quality test recorded successfully");
                      }
                      closeModal();
                      const logs = await poolApi.getWaterQualityLogs({ pool_id: selectedPoolId || undefined });
                      setWaterQualityLogs(Array.isArray(logs) ? logs : []);
                      const poolsRes = await poolApi.getPools();
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to log test");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Pool</label>
                    <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      {(Array.isArray(pools) ? pools : []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">pH Level (Ideal 7.2-7.8) *</label>
                      <input type="number" step="0.1" name="ph_level" required defaultValue="7.4" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Free Chlorine (ppm, Ideal 1.0-3.0) *</label>
                      <input type="number" step="0.1" name="free_chlorine_ppm" required defaultValue="2.0" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Total Chlorine (ppm)</label>
                      <input type="number" step="0.1" name="total_chlorine_ppm" defaultValue="2.2" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Temperature (°C)</label>
                      <input type="number" step="0.5" name="water_temperature_c" defaultValue="28.0" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Water Clarity</label>
                    <select name="water_clarity" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      <option value="clear">Clear (Main drain visible)</option>
                      <option value="cloudy">Cloudy</option>
                      <option value="turbid">Turbid / Murky</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks</label>
                    <textarea name="remarks" placeholder="Observations or dosing applied" className="w-full p-2 border border-slate-200 rounded-lg" rows={2} />
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Submit Log
                    </button>
                  </div>
                </form>
              )}

              {/* --- RETEST MODAL --- */}
              {modalType === "water_retest" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    try {
                      const res = await poolApi.recordWaterRetest(modalData.id, {
                        retest_ph: formData.get("retest_ph"),
                        retest_chlorine: formData.get("retest_chlorine"),
                        corrective_action_taken: formData.get("corrective_action_taken")
                      });
                      if (res.pool_reopened) {
                        toast.success("Retest PASSED! Pool has been reopened automatically.");
                      } else {
                        toast.error("Retest failed. Water parameters remain out of safe range.");
                      }
                      closeModal();
                      const logs = await poolApi.getWaterQualityLogs({ pool_id: selectedPoolId || undefined });
                      setWaterQualityLogs(Array.isArray(logs) ? logs : []);
                      const poolsRes = await poolApi.getPools();
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to record retest");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <p className="text-xs text-slate-500">
                    Recording follow-up test after corrective chemical treatment for log #{modalData.id}.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Retest pH (Ideal 7.2-7.8) *</label>
                      <input type="number" step="0.1" name="retest_ph" required defaultValue="7.4" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Retest Free Cl (ppm, Ideal 1.0-3.0) *</label>
                      <input type="number" step="0.1" name="retest_chlorine" required defaultValue="2.0" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Corrective Action Taken *</label>
                    <textarea name="corrective_action_taken" required placeholder="e.g. Added 2kg Sodium Bisulphate to lower pH" className="w-full p-2 border border-slate-200 rounded-lg" rows={2} />
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Submit Retest
                    </button>
                  </div>
                </form>
              )}

              {/* --- ADD / EDIT POOL MODAL --- */}
              {(modalType === "add_pool" || modalType === "edit_pool") && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      name: formData.get("name"),
                      pool_type: formData.get("pool_type") || "outdoor_adult",
                      location: formData.get("location") || "",
                      max_capacity: Number(formData.get("max_capacity") || 50),
                      volume_liters: Number(formData.get("volume_liters") || 100000),
                      length_meters: formData.get("length_meters") ? Number(formData.get("length_meters")) : null,
                      width_meters: formData.get("width_meters") ? Number(formData.get("width_meters")) : null,
                      depth_min_meters: formData.get("depth_min_meters") ? Number(formData.get("depth_min_meters")) : null,
                      depth_max_meters: formData.get("depth_max_meters") ? Number(formData.get("depth_max_meters")) : null,
                      opening_time: formData.get("opening_time") || "06:00",
                      closing_time: formData.get("closing_time") || "21:00",
                      adult_ticket_price: Number(formData.get("adult_ticket_price") || 500),
                      child_ticket_price: Number(formData.get("child_ticket_price") || 250),
                      status: formData.get("status") || "open",
                      guest_policy: formData.get("guest_policy") || "",
                      description: formData.get("description") || ""
                    };

                    try {
                      if (modalType === "edit_pool" && modalData?.id) {
                        await poolApi.updatePool(modalData.id, payload);
                        toast.success("Pool updated successfully");
                      } else {
                        await poolApi.createPool(payload);
                        toast.success("New pool created successfully");
                      }
                      closeModal();
                      const updatedPools = await poolApi.getPools();
                      setPools(Array.isArray(updatedPools) ? updatedPools : []);
                      await fetchTabData();
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to save pool");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool Name *</label>
                      <input
                        name="name"
                        required
                        defaultValue={modalData?.name || ""}
                        placeholder="e.g. Main Garden Pool"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool Category / Type</label>
                      <select name="pool_type" defaultValue={modalData?.pool_type || "outdoor_adult"} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="outdoor_adult">Outdoor Adult / Lap Pool</option>
                        <option value="indoor_heated">Indoor Heated Pool</option>
                        <option value="kids_splash">Kids Splash & Shallow Pool</option>
                        <option value="jacuzzi">Jacuzzi / Thermal Spa Pool</option>
                        <option value="infinity_rooftop">Rooftop Infinity Pool</option>
                        <option value="plunge">Cold Plunge Pool</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Location / Zone</label>
                      <input
                        name="location"
                        defaultValue={modalData?.location || ""}
                        placeholder="e.g. Ground Deck, Wing A"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Operational Status</label>
                      <select name="status" defaultValue={modalData?.status || "open"} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="open">🟢 Open (Available)</option>
                        <option value="temporarily_closed">🟡 Temporarily Closed</option>
                        <option value="maintenance">🔧 Under Maintenance</option>
                        <option value="emergency_closure">🔴 Emergency Closure</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Max Capacity *</label>
                      <input
                        type="number"
                        name="max_capacity"
                        required
                        min={1}
                        defaultValue={modalData?.max_capacity || 50}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Volume (Liters)</label>
                      <input
                        type="number"
                        name="volume_liters"
                        min={100}
                        defaultValue={modalData?.volume_liters || 100000}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Length (Meters)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="length_meters"
                        defaultValue={modalData?.length_meters || 25}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Width (Meters)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="width_meters"
                        defaultValue={modalData?.width_meters || 12}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Min Depth (m)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="depth_min_meters"
                        defaultValue={modalData?.depth_min_meters || 1.2}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Max Depth (m)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="depth_max_meters"
                        defaultValue={modalData?.depth_max_meters || 2.4}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Opening Time</label>
                      <input
                        type="time"
                        name="opening_time"
                        defaultValue={modalData?.opening_time || "06:00"}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Closing Time</label>
                      <input
                        type="time"
                        name="closing_time"
                        defaultValue={modalData?.closing_time || "21:00"}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Adult Ticket Price ({currency})</label>
                      <input
                        type="number"
                        name="adult_ticket_price"
                        defaultValue={modalData?.adult_ticket_price || 500}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Child Ticket Price ({currency})</label>
                      <input
                        type="number"
                        name="child_ticket_price"
                        defaultValue={modalData?.child_ticket_price || 250}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Guest Entry Policy & Rules</label>
                    <input
                      name="guest_policy"
                      defaultValue={modalData?.guest_policy || ""}
                      placeholder="e.g. Complimentary for in-house hotel residents, ticket required for external visitors"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Description / Notes</label>
                    <textarea
                      name="description"
                      defaultValue={modalData?.description || ""}
                      placeholder="Pool features, heated status, filtration notes..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      {modalType === "edit_pool" ? "Update Pool" : "Save Pool"}
                    </button>
                  </div>
                </form>
              )}

              {/* --- ADD / EDIT MEMBERSHIP PLAN MODAL --- */}
              {(modalType === "add_plan" || modalType === "edit_plan") && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      name: formData.get("name"),
                      plan_type: formData.get("plan_type") || "individual",
                      duration_type: formData.get("duration_type") || "monthly",
                      duration_days: Number(formData.get("duration_days") || 30),
                      price: Number(formData.get("price") || 0),
                      max_daily_admissions: Number(formData.get("max_daily_admissions") || 1),
                      guest_privileges_count: Number(formData.get("guest_privileges_count") || 0),
                      free_towel_rental: formData.get("free_towel_rental") === "on",
                      free_locker: formData.get("free_locker") === "on",
                      description: formData.get("description") || ""
                    };

                    try {
                      if (modalType === "edit_plan" && modalData?.id) {
                        await poolApi.updateMembershipPlan(modalData.id, payload);
                        toast.success("Membership plan updated");
                      } else {
                        await poolApi.createMembershipPlan(payload);
                        toast.success("Membership plan created");
                      }
                      closeModal();
                      const plans = await poolApi.getMembershipPlans();
                      setMembershipPlans(Array.isArray(plans) ? plans : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to save plan");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Plan Name *</label>
                      <input
                        name="name"
                        required
                        defaultValue={modalData?.name || ""}
                        placeholder="e.g. Monthly Standard Access"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Plan Category</label>
                      <select name="plan_type" defaultValue={modalData?.plan_type || "individual"} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="individual">Individual</option>
                        <option value="couple">Couple</option>
                        <option value="family">Family Package</option>
                        <option value="corporate">Corporate Group</option>
                        <option value="student">Student Pass</option>
                        <option value="senior">Senior Citizen</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Duration Billing</label>
                      <select name="duration_type" defaultValue={modalData?.duration_type || "monthly"} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="daily">Daily Pass</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi_annual">Semi-Annual</option>
                        <option value="annual">Annual / Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Validity (Days) *</label>
                      <input
                        type="number"
                        name="duration_days"
                        required
                        min={1}
                        defaultValue={modalData?.duration_days || 30}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Price ({currency}) *</label>
                      <input
                        type="number"
                        name="price"
                        required
                        min={0}
                        defaultValue={modalData?.price || 3500}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-cyan-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Max Daily Admissions</label>
                      <input
                        type="number"
                        name="max_daily_admissions"
                        defaultValue={modalData?.max_daily_admissions || 1}
                        min={1}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Guest Privileges Count</label>
                      <input
                        type="number"
                        name="guest_privileges_count"
                        defaultValue={modalData?.guest_privileges_count || 0}
                        min={0}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-2">
                    <p className="text-xs font-semibold text-slate-700">Included Complimentary Amenities</p>
                    <div className="flex items-center gap-6 text-xs text-slate-600">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="free_towel_rental"
                          defaultChecked={modalData?.free_towel_rental}
                          className="rounded text-cyan-600 focus:ring-cyan-500"
                        />
                        <span>Free Towel Service</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="free_locker"
                          defaultChecked={modalData?.free_locker}
                          className="rounded text-cyan-600 focus:ring-cyan-500"
                        />
                        <span>Free Locker Access</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Description / Notes</label>
                    <textarea
                      name="description"
                      defaultValue={modalData?.description || ""}
                      placeholder="Outline perks, pool access restrictions, family member counts..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      {modalType === "edit_plan" ? "Update Plan" : "Create Plan"}
                    </button>
                  </div>
                </form>
              )}

              {/* --- REGISTER NEW MEMBER MODAL --- */}
              {modalType === "new_member" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      plan_id: formData.get("plan_id"),
                      member_name: formData.get("member_name"),
                      member_phone: formData.get("member_phone"),
                      member_email: formData.get("member_email") || null,
                      emergency_contact_name: formData.get("emergency_contact_name") || null,
                      emergency_contact_phone: formData.get("emergency_contact_phone") || null,
                      start_date: formData.get("start_date") || new Date().toISOString().slice(0, 10),
                      total_amount: Number(formData.get("total_amount") || 0),
                      paid_amount: Number(formData.get("paid_amount") || 0),
                      payment_method: formData.get("payment_method") || "cash",
                      medical_notes: formData.get("medical_notes") || null,
                      notes: formData.get("notes") || null
                    };

                    try {
                      const res = await poolApi.createMembership(payload);
                      toast.success(`Member registered! Membership #${res.membership_number}`);
                      closeModal();
                      const mems = await poolApi.getMemberships();
                      setMemberships(Array.isArray(mems) ? mems : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to register member");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Select Membership Plan *</label>
                    <select
                      name="plan_id"
                      required
                      defaultValue={modalData?.plan_id || membershipPlans[0]?.id}
                      onChange={(e) => {
                        const plan = membershipPlans.find(p => String(p.id) === String(e.target.value));
                        if (plan) {
                          const totalInput = document.getElementById("pool_member_total_amount");
                          const paidInput = document.getElementById("pool_member_paid_amount");
                          if (totalInput) totalInput.value = plan.price;
                          if (paidInput) paidInput.value = plan.price;
                        }
                      }}
                      className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50"
                    >
                      {(Array.isArray(membershipPlans) ? membershipPlans : []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} - {currency} {Number(p.price).toLocaleString()} ({p.duration_days} days)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Member Full Name *</label>
                      <input
                        name="member_name"
                        required
                        placeholder="John Doe"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Phone Number *</label>
                      <input
                        name="member_phone"
                        required
                        placeholder="98XXXXXXXX"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
                      <input
                        type="email"
                        name="member_email"
                        placeholder="john@example.com"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Membership Start Date</label>
                      <input
                        type="date"
                        name="start_date"
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Emergency Contact Person</label>
                      <input
                        name="emergency_contact_name"
                        placeholder="Kin / Spouse / Guardian"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Emergency Contact Phone</label>
                      <input
                        name="emergency_contact_phone"
                        placeholder="Phone number"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Plan Total ({currency})</label>
                      <input
                        id="pool_member_total_amount"
                        type="number"
                        name="total_amount"
                        defaultValue={modalData?.price || membershipPlans[0]?.price || 3500}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Paid Amount ({currency})</label>
                      <input
                        id="pool_member_paid_amount"
                        type="number"
                        name="paid_amount"
                        defaultValue={modalData?.price || membershipPlans[0]?.price || 3500}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-emerald-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Method</label>
                      <select name="payment_method" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="qr_pay">QR Payment</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Medical / Health Notes</label>
                      <textarea
                        name="medical_notes"
                        placeholder="Allergies, asthma, skin conditions, swimming clearance..."
                        className="w-full p-2 border border-slate-200 rounded-lg"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks & Special Privileges</label>
                      <textarea
                        name="notes"
                        placeholder="Locker assignment, referral info, RFID card tag..."
                        className="w-full p-2 border border-slate-200 rounded-lg"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Register & Activate
                    </button>
                  </div>
                </form>
              )}

              {/* --- RENEW MEMBER MODAL --- */}
              {modalType === "renew_member" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      additional_days: Number(formData.get("additional_days") || 30),
                      amount: Number(formData.get("amount") || 0),
                      payment_method: formData.get("payment_method") || "cash"
                    };

                    try {
                      await poolApi.renewMembership(modalData.id, payload);
                      toast.success(`Membership renewed for ${modalData.member_name}`);
                      closeModal();
                      const mems = await poolApi.getMemberships();
                      setMemberships(Array.isArray(mems) ? mems : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to renew membership");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-cyan-50 border border-cyan-100 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-cyan-900 text-sm">{modalData.member_name}</div>
                    <div className="text-cyan-700">Card #: <span className="font-mono font-semibold">{modalData.membership_number}</span></div>
                    <div className="text-cyan-700">Plan: <span className="font-semibold">{modalData.plan_name}</span></div>
                    <div className="text-cyan-700">Current Expiry: <span className="font-semibold">{modalData.expiry_date}</span></div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Extend Validity By (Days) *</label>
                      <input
                        type="number"
                        name="additional_days"
                        required
                        min={1}
                        defaultValue={30}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Renewal Fee ({currency}) *</label>
                      <input
                        type="number"
                        name="amount"
                        required
                        min={0}
                        defaultValue={modalData.total_amount || 3000}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-emerald-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Method</label>
                    <select name="payment_method" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      <option value="cash">Cash</option>
                      <option value="card">Credit / Debit Card</option>
                      <option value="qr_pay">QR Payment</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Confirm Renewal
                    </button>
                  </div>
                </form>
              )}

              {/* --- FREEZE MEMBER MODAL --- */}
              {modalType === "freeze_member" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    try {
                      await poolApi.freezeMembership(modalData.id, formData.get("reason"));
                      toast.success(`Membership for ${modalData.member_name} has been frozen`);
                      closeModal();
                      const mems = await poolApi.getMemberships();
                      setMemberships(Array.isArray(mems) ? mems : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to freeze membership");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-amber-900">{modalData.member_name}</div>
                    <div className="text-amber-800">Card #: <span className="font-mono">{modalData.membership_number}</span></div>
                    <p className="text-amber-700 mt-1">Freezing pauses membership benefits until unfrozen by manager.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Reason for Freezing *</label>
                    <textarea
                      name="reason"
                      required
                      placeholder="e.g. Member medical absence / temporary travel"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={3}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg shadow-sm">
                      Freeze Membership
                    </button>
                  </div>
                </form>
              )}

              {/* --- ADD CLEANING SCHEDULE MODAL --- */}
              {modalType === "add_cleaning_schedule" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      task_name: formData.get("task_name"),
                      frequency: formData.get("frequency") || "daily",
                      scheduled_time: formData.get("scheduled_time") || "06:30",
                      assigned_staff_name: formData.get("assigned_staff_name") || null,
                      checklist_items: formData.get("checklist_items") || null
                    };

                    try {
                      await poolApi.createCleaningSchedule(payload);
                      toast.success("Cleaning schedule created");
                      closeModal();
                      const scheds = await poolApi.getCleaningSchedules(selectedPoolId || undefined);
                      setCleaningSchedules(Array.isArray(scheds) ? scheds : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to schedule task");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Pool *</label>
                    <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                      {(Array.isArray(pools) ? pools : []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Task Name *</label>
                    <input
                      name="task_name"
                      required
                      placeholder="e.g. Skimmer Baskets Debris Cleaning & Vacuuming"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Frequency</label>
                      <select name="frequency" defaultValue="daily" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="twice_daily">Twice Daily</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi_weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Scheduled Time</label>
                      <input
                        type="time"
                        name="scheduled_time"
                        defaultValue="06:30"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Assigned Staff / Attendant</label>
                    <input
                      name="assigned_staff_name"
                      placeholder="e.g. Morning Pool Attendant"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Checklist Instructions</label>
                    <textarea
                      name="checklist_items"
                      placeholder="1. Power off pump. 2. Remove leaves. 3. Flush baskets with hose. 4. Check suction pressure."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={3}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Create Schedule
                    </button>
                  </div>
                </form>
              )}

              {/* --- COMPLETE CLEANING TASK MODAL --- */}
              {modalType === "complete_cleaning" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      completed_by: formData.get("completed_by"),
                      checklist_results: formData.get("checklist_results"),
                      remarks: formData.get("remarks")
                    };

                    try {
                      await poolApi.completeCleaningTask(modalData.id, payload);
                      toast.success("Cleaning task completed successfully");
                      closeModal();
                      const [scheds, logs] = await Promise.all([
                        poolApi.getCleaningSchedules(selectedPoolId || undefined),
                        poolApi.getCleaningLogs({ pool_id: selectedPoolId || undefined })
                      ]);
                      setCleaningSchedules(Array.isArray(scheds) ? scheds : []);
                      setCleaningLogs(Array.isArray(logs) ? logs : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to mark task completed");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-slate-800 text-sm">{modalData.task_name}</div>
                    <div className="text-slate-600">Pool: <span className="font-semibold">{modalData.pool_name}</span> • Frequency: <span className="capitalize">{modalData.frequency}</span></div>
                    {modalData.checklist_items && (
                      <p className="text-slate-500 mt-1 italic">Instructions: {modalData.checklist_items}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Completed By *</label>
                    <input
                      name="completed_by"
                      required
                      defaultValue={modalData.assigned_staff_name || "Pool Attendant"}
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Checklist Execution Status</label>
                    <input
                      name="checklist_results"
                      defaultValue="All checklist items completed satisfactorily"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks / Issues Observed</label>
                    <textarea
                      name="remarks"
                      placeholder="e.g. Pump pressure nominal, minor leaf buildup near deep end"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm">
                      Mark Completed
                    </button>
                  </div>
                </form>
              )}

              {/* --- ADD MAINTENANCE RECORD MODAL --- */}
              {modalType === "add_maintenance" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      equipment_name: formData.get("equipment_name"),
                      maintenance_type: formData.get("maintenance_type") || "routine",
                      priority: formData.get("priority") || "medium",
                      issue_description: formData.get("issue_description"),
                      assigned_to: formData.get("assigned_to") || null,
                      vendor_name: formData.get("vendor_name") || null,
                      vendor_contact: formData.get("vendor_contact") || null,
                      scheduled_date: formData.get("scheduled_date") || null,
                      cost: Number(formData.get("cost") || 0)
                    };

                    try {
                      await poolApi.createMaintenanceRecord(payload);
                      toast.success("Maintenance ticket created");
                      closeModal();
                      const records = await poolApi.getMaintenanceRecords({ pool_id: selectedPoolId || undefined });
                      setMaintenanceRecords(Array.isArray(records) ? records : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to create maintenance ticket");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Target Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Equipment / Facility Asset *</label>
                      <input
                        name="equipment_name"
                        required
                        placeholder="e.g. Circulation Pump #1 / Sand Filter"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Maintenance Type</label>
                      <select name="maintenance_type" defaultValue="routine" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="routine">Routine Inspection</option>
                        <option value="preventative">Preventative Servicing</option>
                        <option value="corrective">Corrective Repair</option>
                        <option value="emergency_repair">Emergency Repair</option>
                        <option value="overhaul">Equipment Overhaul</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Priority</label>
                      <select name="priority" defaultValue="medium" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent / Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Issue Description *</label>
                    <textarea
                      name="issue_description"
                      required
                      placeholder="Describe symptoms, strange noises, leaks, pressure gauge drop, electrical trips..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Assigned Staff</label>
                      <input
                        name="assigned_to"
                        placeholder="In-house Tech"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Vendor Name</label>
                      <input
                        name="vendor_name"
                        placeholder="Contractor / Supplier"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Vendor Contact</label>
                      <input
                        name="vendor_contact"
                        placeholder="Phone / Email"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Scheduled Date</label>
                      <input
                        type="date"
                        name="scheduled_date"
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Estimated Cost ({currency})</label>
                      <input
                        type="number"
                        name="cost"
                        defaultValue={0}
                        min={0}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Record Ticket
                    </button>
                  </div>
                </form>
              )}

              {/* --- UPDATE / RESOLVE MAINTENANCE MODAL --- */}
              {modalType === "update_maintenance" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      status: formData.get("status"),
                      downtime_hours: Number(formData.get("downtime_hours") || 0),
                      spare_parts_used: formData.get("spare_parts_used") || null,
                      cost: Number(formData.get("cost") || 0),
                      resolution_notes: formData.get("resolution_notes") || null
                    };

                    try {
                      await poolApi.updateMaintenanceRecord(modalData.id, payload);
                      toast.success("Maintenance status updated");
                      closeModal();
                      const records = await poolApi.getMaintenanceRecords({ pool_id: selectedPoolId || undefined });
                      setMaintenanceRecords(Array.isArray(records) ? records : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to update maintenance");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-slate-800 text-sm">{modalData.equipment_name}</div>
                    <div className="text-slate-600">Pool: <span className="font-semibold">{modalData.pool_name}</span> • Priority: <span className="uppercase font-semibold text-amber-700">{modalData.priority}</span></div>
                    <p className="text-slate-500 mt-1">Issue: {modalData.issue_description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
                      <select name="status" defaultValue={modalData.status || "in_progress"} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="reported">Reported</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting_parts">Waiting on Spare Parts</option>
                        <option value="completed">Completed / Resolved</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Facility Downtime (Hours)</label>
                      <input
                        type="number"
                        step="0.5"
                        name="downtime_hours"
                        defaultValue={modalData.downtime_hours || 0}
                        min={0}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Spare Parts Replaced</label>
                      <input
                        name="spare_parts_used"
                        defaultValue={modalData.spare_parts_used || ""}
                        placeholder="e.g. Mechanical seal, O-ring"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Total Actual Cost ({currency})</label>
                      <input
                        type="number"
                        name="cost"
                        defaultValue={modalData.cost || 0}
                        min={0}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Resolution & Work Performed</label>
                    <textarea
                      name="resolution_notes"
                      defaultValue={modalData.resolution_notes || ""}
                      placeholder="Actions taken to fix issue, testing results..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={3}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Update Ticket
                    </button>
                  </div>
                </form>
              )}

              {/* --- NEW SAFETY INSPECTION MODAL --- */}
              {modalType === "new_safety_inspection" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      inspection_type: formData.get("inspection_type") || "opening_checklist",
                      lifeguard_on_duty: formData.get("lifeguard_on_duty"),
                      inspection_date: formData.get("inspection_date") || new Date().toISOString().slice(0, 10),
                      rescue_tubes_available: formData.get("rescue_tubes_available") === "on",
                      ring_buoys_in_place: formData.get("ring_buoys_in_place") === "on",
                      first_aid_kit_stocked: formData.get("first_aid_kit_stocked") === "on",
                      spine_board_accessible: formData.get("spine_board_accessible") === "on",
                      depth_markers_visible: formData.get("depth_markers_visible") === "on",
                      emergency_shutoff_tested: formData.get("emergency_shutoff_tested") === "on",
                      pool_rules_signage_clear: formData.get("pool_rules_signage_clear") === "on",
                      slip_hazards_checked: formData.get("slip_hazards_checked") === "on",
                      water_clarity_acceptable: formData.get("water_clarity_acceptable") === "on",
                      safety_status: formData.get("safety_status") || "passed",
                      notes: formData.get("notes") || null
                    };

                    try {
                      await poolApi.createSafetyInspection(payload);
                      toast.success("Safety inspection checklist logged");
                      closeModal();
                      const insps = await poolApi.getSafetyInspections({ pool_id: selectedPoolId || undefined });
                      setSafetyInspections(Array.isArray(insps) ? insps : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to log inspection");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Inspection Routine</label>
                      <select name="inspection_type" defaultValue="opening_checklist" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="opening_checklist">Morning Facility Opening Checklist</option>
                        <option value="closing_checklist">Evening Facility Closing Checklist</option>
                        <option value="periodic_audit">Comprehensive Safety Audit</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Duty Lifeguard / Inspector *</label>
                      <input
                        name="lifeguard_on_duty"
                        required
                        defaultValue="Duty Lifeguard"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Inspection Date</label>
                      <input
                        type="date"
                        name="inspection_date"
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
                    <p className="text-xs font-bold text-slate-700">Safety Compliance Items</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="rescue_tubes_available" defaultChecked className="rounded text-cyan-600" />
                        <span>Rescue Tubes in Position</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="ring_buoys_in_place" defaultChecked className="rounded text-cyan-600" />
                        <span>Ring Buoys & Lines Mounted</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="first_aid_kit_stocked" defaultChecked className="rounded text-cyan-600" />
                        <span>First Aid Kit Restocked</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="spine_board_accessible" defaultChecked className="rounded text-cyan-600" />
                        <span>Spine Board & Straps Ready</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="depth_markers_visible" defaultChecked className="rounded text-cyan-600" />
                        <span>Depth Markings Clear</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="emergency_shutoff_tested" defaultChecked className="rounded text-cyan-600" />
                        <span>Emergency Shut-off Tested</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="pool_rules_signage_clear" defaultChecked className="rounded text-cyan-600" />
                        <span>Pool Rules Signage Visible</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200">
                        <input type="checkbox" name="slip_hazards_checked" defaultChecked className="rounded text-cyan-600" />
                        <span>Deck Free of Slip Hazards</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200 sm:col-span-2">
                        <input type="checkbox" name="water_clarity_acceptable" defaultChecked className="rounded text-cyan-600" />
                        <span>Water Clarity Acceptable (Main drain clearly visible)</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Overall Inspection Outcome</label>
                      <select name="safety_status" defaultValue="passed" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50 font-semibold">
                        <option value="passed">🟢 Passed (Safe to Operate)</option>
                        <option value="passed_with_defects">🟡 Passed with Minor Defects</option>
                        <option value="failed">🔴 Failed (Hazardous - Close Pool)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Inspector Notes</label>
                      <textarea
                        name="notes"
                        placeholder="Detail any defects or supplies ordered..."
                        className="w-full p-2 border border-slate-200 rounded-lg"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm">
                      Submit Inspection
                    </button>
                  </div>
                </form>
              )}

              {/* --- REPORT INCIDENT MODAL --- */}
              {modalType === "new_incident" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      incident_time: formData.get("incident_time") || new Date().toISOString(),
                      severity: formData.get("severity") || "minor",
                      incident_type: formData.get("incident_type") || "slip_fall",
                      person_involved_type: formData.get("person_involved_type") || "guest",
                      person_name: formData.get("person_name") || null,
                      person_phone: formData.get("person_phone") || null,
                      room_number: formData.get("room_number") || null,
                      description: formData.get("description"),
                      immediate_action_taken: formData.get("immediate_action_taken"),
                      first_aid_administered: formData.get("first_aid_administered") === "on",
                      emergency_services_called: formData.get("emergency_services_called") === "on",
                      pool_temporarily_closed: formData.get("pool_temporarily_closed") === "on",
                      lifeguard_attendant_name: formData.get("lifeguard_attendant_name") || "Duty Lifeguard"
                    };

                    try {
                      const res = await poolApi.createIncident(payload);
                      toast.success(`Incident reported #${res.incident_number}`);
                      closeModal();
                      const [incs, poolsRes] = await Promise.all([
                        poolApi.getIncidents({ pool_id: selectedPoolId || undefined }),
                        poolApi.getPools()
                      ]);
                      setIncidents(Array.isArray(incs) ? incs : []);
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to log incident");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Severity *</label>
                      <select name="severity" defaultValue="minor" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50 font-bold">
                        <option value="minor">Minor (Treated on deck)</option>
                        <option value="moderate">Moderate (Requires observation)</option>
                        <option value="severe">Severe (Medical attention required)</option>
                        <option value="critical">Critical / Life Hazard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Incident Type *</label>
                      <select name="incident_type" defaultValue="slip_fall" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="slip_fall">Slip & Fall</option>
                        <option value="near_drowning">Near Drowning / Water Rescue</option>
                        <option value="minor_cut">Minor Cut / Scrape</option>
                        <option value="water_contamination">Water Contamination (AFR / Biohazard)</option>
                        <option value="equipment_injury">Equipment Related Injury</option>
                        <option value="behavioral">Rule Violation / Behavioral</option>
                        <option value="other">Other Event</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Person Category</label>
                      <select name="person_involved_type" defaultValue="guest" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="guest">Hotel Resident Guest</option>
                        <option value="member">Pool Member</option>
                        <option value="visitor">Walk-in Visitor</option>
                        <option value="staff">Hotel Staff / Lifeguard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Person Name</label>
                      <input name="person_name" placeholder="Full name" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Phone</label>
                      <input name="person_phone" placeholder="Phone" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Room # (if guest)</label>
                      <input name="room_number" placeholder="e.g. 302" className="w-full p-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Incident Description *</label>
                    <textarea
                      name="description"
                      required
                      placeholder="Detailed factual statement of what happened..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Immediate Action Taken *</label>
                    <textarea
                      name="immediate_action_taken"
                      required
                      placeholder="What was done right away (e.g. ice pack, sanitized deck, pool cleared)..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="bg-red-50 p-3 rounded-lg border border-red-200 space-y-2">
                    <p className="text-xs font-bold text-red-900">Emergency Actions & Escalations</p>
                    <div className="flex flex-wrap items-center gap-5 text-xs text-red-800">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" name="first_aid_administered" className="rounded text-red-600" />
                        <span>First Aid Administered</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" name="emergency_services_called" className="rounded text-red-600" />
                        <span>Emergency Services / Ambulance Called</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer font-bold">
                        <input type="checkbox" name="pool_temporarily_closed" className="rounded text-red-600" />
                        <span>Pool Temporarily Closed</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Duty Lifeguard / Reporter</label>
                    <input
                      name="lifeguard_attendant_name"
                      defaultValue="Duty Lifeguard"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-sm">
                      Submit Incident Report
                    </button>
                  </div>
                </form>
              )}

              {/* --- REOPEN / RESOLVE INCIDENT MODAL --- */}
              {modalType === "reopen_incident" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    try {
                      await poolApi.authorizeReopeningIncident(modalData.id, {
                        corrective_actions_preventative: formData.get("corrective_actions_preventative")
                      });
                      toast.success("Incident resolved and facility authorized for regular operations");
                      closeModal();
                      const [incs, poolsRes] = await Promise.all([
                        poolApi.getIncidents({ pool_id: selectedPoolId || undefined }),
                        poolApi.getPools()
                      ]);
                      setIncidents(Array.isArray(incs) ? incs : []);
                      setPools(Array.isArray(poolsRes) ? poolsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to authorize resolution");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-slate-800 text-sm">Incident #{modalData.incident_number}</div>
                    <div className="text-slate-600">Pool: <span className="font-semibold">{modalData.pool_name}</span> • Severity: <span className="font-bold text-red-600 uppercase">{modalData.severity}</span></div>
                    <p className="text-slate-500 mt-1">Details: {modalData.description}</p>
                    <p className="text-slate-500">Immediate Action: {modalData.immediate_action_taken}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Corrective & Preventative Actions Completed *</label>
                    <textarea
                      name="corrective_actions_preventative"
                      required
                      placeholder="e.g. Deck anti-slip treatment applied, pool superchlorinated and water parameters confirmed safe, staff briefed..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={3}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm">
                      Authorize Reopening & Close Ticket
                    </button>
                  </div>
                </form>
              )}

              {/* --- LOG CHEMICAL DOSING MODAL --- */}
              {modalType === "log_chemical_dosing" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      inventory_item_id: formData.get("inventory_item_id") || null,
                      chemical_name: formData.get("chemical_name"),
                      quantity_consumed: Number(formData.get("quantity_consumed")),
                      unit: formData.get("unit") || "kg",
                      purpose: formData.get("purpose") || "routine_dosing",
                      administered_by: formData.get("administered_by") || "Pool Attendant",
                      notes: formData.get("notes") || null
                    };

                    try {
                      await poolApi.recordChemicalConsumption(payload);
                      toast.success("Chemical dosing logged");
                      closeModal();
                      const chems = await poolApi.getChemicalConsumptions({ pool_id: selectedPoolId || undefined });
                      setChemicals(Array.isArray(chems) ? chems : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to log chemical dosing");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Target Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Central Inventory Link (Deducts Stock)</label>
                      <select
                        name="inventory_item_id"
                        onChange={(e) => {
                          const item = inventoryItems.find(i => String(i.id) === String(e.target.value));
                          if (item) {
                            const nameField = document.getElementById("chemical_name_input");
                            if (nameField) nameField.value = item.name || item.title || "";
                          }
                        }}
                        className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50"
                      >
                        <option value="">None / Manual Chemical Log</option>
                        {(Array.isArray(inventoryItems) ? inventoryItems : []).map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name || i.title} (Stock: {i.quantity || 0} {i.unit || ''})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Chemical Product Name *</label>
                      <input
                        id="chemical_name_input"
                        name="chemical_name"
                        required
                        placeholder="e.g. Granular Chlorine 70% (HTH)"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Unit of Measure</label>
                      <select name="unit" defaultValue="kg" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="kg">Kilograms (kg)</option>
                        <option value="liters">Liters (L)</option>
                        <option value="tablets">Tablets (Pcs)</option>
                        <option value="grams">Grams (g)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Quantity Dosed *</label>
                      <input
                        type="number"
                        step="0.01"
                        name="quantity_consumed"
                        required
                        min={0.01}
                        placeholder="e.g. 1.5"
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-cyan-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Dosing Purpose</label>
                      <select name="purpose" defaultValue="routine_dosing" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="routine_dosing">Routine Sanitation Dosing</option>
                        <option value="shock_chlorination">Shock Chlorination (Superchlorination)</option>
                        <option value="ph_balancing">pH Neutralization (Plus/Minus)</option>
                        <option value="algae_treatment">Algaecide Treatment</option>
                        <option value="clarifier">Flocculant / Water Clarifier</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Administered By</label>
                      <input
                        name="administered_by"
                        defaultValue="Pool Technician"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks / Pre-Dosing Reading</label>
                      <input
                        name="notes"
                        placeholder="e.g. Free Cl was 0.8 ppm before shock treatment"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Record Dosing
                    </button>
                  </div>
                </form>
              )}

              {/* --- ADD LESSON BATCH MODAL --- */}
              {modalType === "add_lesson" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      title: formData.get("title"),
                      instructor_name: formData.get("instructor_name"),
                      instructor_phone: formData.get("instructor_phone") || null,
                      session_type: formData.get("session_type") || "group",
                      skill_level: formData.get("skill_level") || "beginner",
                      max_students: Number(formData.get("max_students") || 10),
                      price_per_student: Number(formData.get("price_per_student") || 0),
                      schedule_days: formData.get("schedule_days") || "Mon, Wed, Fri",
                      start_time: formData.get("start_time") || "07:00",
                      end_time: formData.get("end_time") || "08:00",
                      start_date: formData.get("start_date"),
                      end_date: formData.get("end_date"),
                      description: formData.get("description") || null
                    };

                    try {
                      await poolApi.createLesson(payload);
                      toast.success("Swimming coaching batch created");
                      closeModal();
                      const lessonsRes = await poolApi.getLessons({ pool_id: selectedPoolId || undefined });
                      setLessons(Array.isArray(lessonsRes) ? lessonsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to create lesson batch");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Batch Course Title *</label>
                      <input
                        name="title"
                        required
                        placeholder="e.g. Kids Beginner Swimming Batch A"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Instructor / Coach *</label>
                      <input
                        name="instructor_name"
                        required
                        placeholder="Coach Name"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Instructor Phone</label>
                      <input
                        name="instructor_phone"
                        placeholder="Phone number"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Session Type</label>
                      <select name="session_type" defaultValue="group" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="group">Group Batch</option>
                        <option value="private">Private 1-on-1</option>
                        <option value="semi_private">Semi-Private</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Skill Level</label>
                      <select name="skill_level" defaultValue="beginner" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced / Stroke Clinic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Max Students</label>
                      <input
                        type="number"
                        name="max_students"
                        defaultValue={10}
                        min={1}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Fee / Student ({currency}) *</label>
                      <input
                        type="number"
                        name="price_per_student"
                        required
                        defaultValue={3000}
                        min={0}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-cyan-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Schedule Days</label>
                      <input
                        name="schedule_days"
                        defaultValue="Mon, Wed, Fri"
                        placeholder="e.g. Mon, Wed, Fri"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Start Time</label>
                      <input
                        type="time"
                        name="start_time"
                        defaultValue="07:00"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">End Time</label>
                      <input
                        type="time"
                        name="end_time"
                        defaultValue="08:00"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Course Start Date *</label>
                      <input
                        type="date"
                        name="start_date"
                        required
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Course End Date *</label>
                      <input
                        type="date"
                        name="end_date"
                        required
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Course Curriculum & Details</label>
                    <textarea
                      name="description"
                      placeholder="Outline skills covered, water safety tips, equipment needed..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Create Batch
                    </button>
                  </div>
                </form>
              )}

              {/* --- ENROLL STUDENT MODAL --- */}
              {modalType === "enroll_student" && modalData && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      student_name: formData.get("student_name"),
                      student_age: Number(formData.get("student_age") || 0),
                      guardian_name: formData.get("guardian_name") || null,
                      guardian_phone: formData.get("guardian_phone"),
                      total_fee: Number(formData.get("total_fee") || 0),
                      paid_amount: Number(formData.get("paid_amount") || 0),
                      payment_method: formData.get("payment_method") || "cash"
                    };

                    try {
                      await poolApi.enrollStudent(modalData.id, payload);
                      toast.success(`Student enrolled in ${modalData.title}`);
                      closeModal();
                      const lessonsRes = await poolApi.getLessons({ pool_id: selectedPoolId || undefined });
                      setLessons(Array.isArray(lessonsRes) ? lessonsRes : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to enroll student");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="bg-cyan-50 border border-cyan-100 p-3 rounded-lg text-xs space-y-1">
                    <div className="font-bold text-cyan-900 text-sm">{modalData.title}</div>
                    <div className="text-cyan-700">Coach: <span className="font-semibold">{modalData.instructor_name}</span></div>
                    <div className="text-cyan-700">Schedule: {modalData.schedule_days} ({modalData.start_time} - {modalData.end_time})</div>
                    <div className="text-cyan-800 font-bold">Standard Fee: {currency} {Number(modalData.price_per_student).toLocaleString()}</div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Student Full Name *</label>
                      <input
                        name="student_name"
                        required
                        placeholder="Student name"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Student Age</label>
                      <input
                        type="number"
                        name="student_age"
                        defaultValue={10}
                        min={3}
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Parent / Guardian Name</label>
                      <input
                        name="guardian_name"
                        placeholder="Guardian full name"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Guardian / Contact Phone *</label>
                      <input
                        name="guardian_phone"
                        required
                        placeholder="Phone number"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Total Fee ({currency})</label>
                      <input
                        type="number"
                        name="total_fee"
                        defaultValue={modalData.price_per_student || 0}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Paid Amount ({currency})</label>
                      <input
                        type="number"
                        name="paid_amount"
                        defaultValue={modalData.price_per_student || 0}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-emerald-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Method</label>
                      <select name="payment_method" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="qr_pay">QR Payment</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Enroll Student
                    </button>
                  </div>
                </form>
              )}

              {/* --- NEW SHIFT HANDOVER MODAL --- */}
              {modalType === "new_shift_handover" && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      pool_id: formData.get("pool_id") || selectedPoolId,
                      shift_name: formData.get("shift_name") || "Morning to Afternoon",
                      handing_over_staff: formData.get("handing_over_staff"),
                      receiving_staff: formData.get("receiving_staff"),
                      water_quality_status: formData.get("water_quality_status"),
                      equipment_status: formData.get("equipment_status"),
                      active_incidents_notes: formData.get("active_incidents_notes") || null,
                      pending_tasks_for_next_shift: formData.get("pending_tasks_for_next_shift") || null,
                      remarks: formData.get("remarks") || null
                    };

                    try {
                      await poolApi.createShiftHandover(payload);
                      toast.success("Shift handover logged successfully");
                      closeModal();
                      const shifts = await poolApi.getShiftHandovers({ pool_id: selectedPoolId || undefined });
                      setShiftHandovers(Array.isArray(shifts) ? shifts : []);
                    } catch (err) {
                      toast.error(err.response?.data?.message || err.message || "Failed to log handover");
                    }
                  }}
                  className="space-y-4 text-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pool *</label>
                      <select name="pool_id" defaultValue={selectedPoolId} className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        {(Array.isArray(pools) ? pools : []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Shift Turnover</label>
                      <select name="shift_name" defaultValue="Morning to Afternoon" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="Morning to Afternoon">Morning to Afternoon</option>
                        <option value="Afternoon to Evening">Afternoon to Evening</option>
                        <option value="Evening to Night / Closing">Evening to Night / Closing</option>
                        <option value="Night to Opening">Night to Opening</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Outgoing Lifeguard / Attendant *</label>
                      <input
                        name="handing_over_staff"
                        required
                        defaultValue="Outgoing Attendant"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Incoming Lifeguard / Attendant *</label>
                      <input
                        name="receiving_staff"
                        required
                        defaultValue="Incoming Attendant"
                        className="w-full p-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Water Quality Status</label>
                      <select name="water_quality_status" defaultValue="Normal" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="Normal">Normal (Parameters within safe limits)</option>
                        <option value="Treated">Treated (Chemicals recently added)</option>
                        <option value="Requires Retest">Requires Retesting</option>
                        <option value="Temporary Closure">Temporary Closure in Effect</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Equipment Operational Status</label>
                      <select name="equipment_status" defaultValue="Operational" className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50">
                        <option value="Operational">All Systems Fully Operational</option>
                        <option value="Maintenance in Progress">Maintenance in Progress</option>
                        <option value="Filter Backwash Needed">Filter Backwash Scheduled</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Active Incidents / Near-Miss Notices</label>
                    <input
                      name="active_incidents_notes"
                      placeholder="Any slips, medical interventions, or guest warnings during shift"
                      className="w-full p-2 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Pending Priority Tasks for Incoming Shift</label>
                    <textarea
                      name="pending_tasks_for_next_shift"
                      placeholder="e.g. Run 14:00 chlorine test, empty strainer basket, inspect first aid box..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">General Shift Remarks</label>
                    <textarea
                      name="remarks"
                      placeholder="Weather notes, pool deck cleanliness, bather flow observations..."
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      rows={2}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg shadow-sm">
                      Submit Handover
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* PRINTABLE PASS CARD MODAL */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTicketForPrint && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center border border-slate-200">
            <div className="w-12 h-12 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <IconSwimming size={24} />
            </div>
            <h3 className="font-bold text-slate-900 text-lg">Swimming Pool Admission Pass</h3>
            <p className="text-xs text-slate-400 mt-0.5">eHospitality Pool Access Ticket</p>

            <div className="my-5 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">TICKET NUMBER</p>
              <p className="text-xl font-extrabold text-cyan-700 mt-0.5">{activeTicketForPrint.ticket_number}</p>
              
              <div className="my-3 py-2 px-3 bg-white rounded border border-slate-200 text-xs font-mono font-bold text-slate-700 truncate">
                {activeTicketForPrint.qr_code_token}
              </div>

              <div className="text-xs text-left space-y-1 text-slate-600 pt-2 border-t border-slate-200">
                <div className="flex justify-between">
                  <span>Guest:</span>
                  <span className="font-semibold text-slate-800">{activeTicketForPrint.guest_name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Swimmers:</span>
                  <span className="font-semibold text-slate-800">{activeTicketForPrint.total_swimmers} Swimmers</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Paid:</span>
                  <span className="font-bold text-emerald-600">{currency} {Number(activeTicketForPrint.total_amount).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTicketForPrint(null)}
                className="flex-1 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg transition"
              >
                Close
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs rounded-lg transition"
              >
                Print Pass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
